import { FC, useEffect } from 'react';
import { SELECT_EMPTY_VALUE, SelectField } from '@mews-commander/form';
import { WithScope, WithServiceId } from '@mews-commander/types';
import { createZodFormResolver, lt, useFormControls, FormCheckboxField } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { createOption } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useRepositoryQuery } from '@mews-commander/data-access';
import { useSortedResourceCategoriesByServiceId } from '@mews-commander/legacy-entities/src/utils/useResourceCategory';
import { getReservationsByIds } from '@mews-commander/legacy-entities/src/ducks/reservations';
import { useCommanderSelector, useGainsightCommanderEvents } from '@mews-commander/utils';
import { FormLayout } from '../../FormLayout';
import { AssignedCategoryFormValues, AssignedCategoryFormSchema, AssignedCategoryFormKeys } from './types';
import { useAssignedCategoryFormDefaultResourceCategoryId } from './useAssignedCategoryFormDefaultResourceCategoryId';
import { useChangeResourceCategory } from './useChangeResourceCategory';
import { UpdatePriceText } from './UpdatePriceText';

interface AssignedCategoryFormProps extends WithServiceId, WithScope {
    reservationIds: string[];
}

export const AssignedCategoryForm: FC<AssignedCategoryFormProps> = ({ serviceId, reservationIds, scope }) => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    const gainsightEvents = useGainsightCommanderEvents();

    const reservations = useCommanderSelector((state) =>
        getReservationsByIds(state, { ids: reservationIds, enterpriseId: currentEnterpriseId })
    );

    const resourceCategoriesToOptions = useSortedResourceCategoriesByServiceId(serviceId).map((c) =>
        createOption({ value: c.id, label: c.name })
    );

    const defaultCategoryId = useAssignedCategoryFormDefaultResourceCategoryId(reservationIds);

    const { changeResourceCategory, dialog, loading } = useChangeResourceCategory(serviceId, reservations, scope);

    const { renderFormContent, watch, hasDirtyFields, reset, setValue } = useFormControls<AssignedCategoryFormValues>({
        onSubmit: changeResourceCategory,
        resolver: createZodFormResolver(AssignedCategoryFormSchema),
        defaultValues: {
            [AssignedCategoryFormKeys.assignedResourceCategoryId]: defaultCategoryId,
            [AssignedCategoryFormKeys.updatePrice]: false,
            [AssignedCategoryFormKeys.updateRequestedCategory]: false,
        },
        dataTestForm: `space-category-form-${scope}`,
    });

    const selectedAssignedCategoryId = watch(AssignedCategoryFormKeys.assignedResourceCategoryId);
    const updateRequestedCategory = watch(AssignedCategoryFormKeys.updateRequestedCategory);
    const updatePrice = watch(AssignedCategoryFormKeys.updatePrice);

    const { data: priceDiffData } = useRepositoryQuery(
        'reservations',
        'getRequestedCategoryUpdatePriceDifference',
        { reservationIds, resourceCategoryId: selectedAssignedCategoryId },
        {
            enabled: selectedAssignedCategoryId !== SELECT_EMPTY_VALUE && hasDirtyFields,
        }
    );

    const isNoPriceDiff = priceDiffData?.priceDifferenceAmount?.value === 0;

    const updatePriceDisabled = !updateRequestedCategory || isNoPriceDiff;

    useEffect(() => {
        reset({
            [AssignedCategoryFormKeys.assignedResourceCategoryId]: defaultCategoryId,
            [AssignedCategoryFormKeys.updatePrice]: false,
            [AssignedCategoryFormKeys.updateRequestedCategory]: false,
        });
    }, [defaultCategoryId, reset]);

    useEffect(() => {
        if (!updateRequestedCategory && updatePrice) {
            setValue(AssignedCategoryFormKeys.updatePrice, false);
        }
    }, [updateRequestedCategory, updatePrice, setValue]);

    useEffect(() => {
        gainsightEvents.onCheckboxChecked({ context: 'UpdateRequestedCategory', checked: updateRequestedCategory });
    }, [updateRequestedCategory, gainsightEvents]);

    return (
        <>
            {renderFormContent(
                <FormLayout
                    submitButtonDisabled={!hasDirtyFields || selectedAssignedCategoryId === SELECT_EMPTY_VALUE}
                    loading={loading}
                >
                    <SelectField
                        showEmptyValue
                        name={AssignedCategoryFormKeys.assignedResourceCategoryId}
                        options={resourceCategoriesToOptions}
                        label={lt(TextKey.AssignedCategory)}
                    />

                    {selectedAssignedCategoryId && hasDirtyFields && (
                        <Stack vertical spacing="200">
                            <FormCheckboxField
                                name={AssignedCategoryFormKeys.updatePrice}
                                data-test-id={AssignedCategoryFormKeys.updatePrice}
                                disabled={updatePriceDisabled}
                            >
                                <UpdatePriceText
                                    highlightedPrice={!updatePriceDisabled}
                                    priceDiff={priceDiffData?.priceDifferenceAmount}
                                />
                            </FormCheckboxField>

                            <FormCheckboxField
                                name={AssignedCategoryFormKeys.updateRequestedCategory}
                                data-test-id={AssignedCategoryFormKeys.updateRequestedCategory}
                            >
                                {lt('UpdateRequestedCategory')}
                            </FormCheckboxField>
                        </Stack>
                    )}
                </FormLayout>
            )}
            {dialog}
        </>
    );
};
